/**
 * Created by piotr@revisual on 11/08/2025.
 */

import { useUserStore } from "../../../../hooks/redux/user";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { hasSubscription as _hasSubscriotion } from "../../helpers/organization";
import { useEffect, useMemo, useState } from "react";
import { useDomainsStore } from "../../../../hooks/redux/domains";
import { useOrganizationMembersStore } from "../../../../hooks/redux/organizationMembers";
import { UserRoles } from "../../consts/crm";
import useInterval from "../../../../hooks/useInterval";
import { useHistory } from "react-router-dom";

export const useWizardHook = (form) => {
  const [pollSession, setPollSession] = useState(false);
  const [startProcessing, setStartProcessing] = useState(false);
  const [finished, setFinished] = useState(false);

  const [hasDomainsProcessed, setHasDomainsProcessed] = useState(false);
  const [hasMembersProcessed, setHasMembersProcessed] = useState(false);

  const history = useHistory();

  // if user has no subscription, it will poll the session
  // until the subscription is active, then it will set startProcessing to true
  const { user, checkSession } = useUserStore();
  const { currentOrganization, updateOrgSetting } =
    useCurrentOrganizationStore();
  const { organizationMembers, addOrganizationMember } =
    useOrganizationMembersStore();

  const { domains, updateDomain } = useDomainsStore();

  const isProcessing =
    user.checkSession ||
    organizationMembers.add ||
    domains.update ||
    currentOrganization.updateSetting;

  const hasSubscription = useMemo(
    () => _hasSubscriotion(currentOrganization),
    [currentOrganization]
  );

  const finishedProcessing = useMemo(() => {
    return hasSubscription && hasDomainsProcessed && hasMembersProcessed;
  }, [hasSubscription, hasDomainsProcessed, hasMembersProcessed]);

  useInterval(
    () => {
      if (!hasSubscription) {
        checkSession();
      }
    },
    pollSession ? 5000 : null
  );

  useEffect(() => {
    if (pollSession && hasSubscription) {
      setStartProcessing(true);
      setPollSession(false);
    }
  }, [pollSession, hasSubscription]);

  useEffect(() => {
    if (hasSubscription && !isProcessing && startProcessing) {
      if (
        form.websiteDomain &&
        !hasDomainsProcessed &&
        !domains.collection.length &&
        !domains.update
      ) {
        updateDomain(null, { domain: form.websiteDomain, available: 1 });
      } else {
        setHasDomainsProcessed(true);
      }

      if (form.emails.length > 0 && !hasMembersProcessed) {
        form.emails.forEach(async (email) => {
          await addOrganizationMember({
            organization_uuid: currentOrganization.model.uuid,
            invite_email: email,
            role: UserRoles.admin,
          });
        });
      } else {
        setHasMembersProcessed(true);
      }
    }
  }, [
    currentOrganization,
    form,
    hasSubscription,
    isProcessing,
    hasDomainsProcessed,
    hasMembersProcessed,
    startProcessing,
    domains.collection,
  ]);

  useEffect(() => {
    if (domains.updateSuccess) {
      setHasDomainsProcessed(true);
    }
  }, [domains.updateSuccess]);

  useEffect(() => {
    if (organizationMembers.addSuccess) {
      setHasMembersProcessed(true);
    }
  }, [organizationMembers.addSuccess]);

  useEffect(() => {
    if (finishedProcessing) {
      updateOrgSetting("onboarding_completed", 1);

      history.push(`/integrations/connect/${form.integration}`);
    }
  }, [finishedProcessing, history]);

  const finishWizard = () => {
    if (!hasSubscription) {
      setStartProcessing(true);
      setPollSession(true);
    } else {
      setStartProcessing(true);
    }
  };

  return [finishWizard, finished, startProcessing, pollSession];
};

export default useWizardHook;
